/**
 * Attention Pulse — sequential feature-discovery pulses on page load.
 *
 * Lightweight alternative to product tours: after the page loader hides, key
 * controls get a short one-shot box-shadow ring pulse, one after another, so
 * the user's eye walks over them. No overlays, no blocking steps, no text.
 *
 * Targets are declared in PULSE_TARGETS in attention priority order (not DOM
 * order). Each target is pulsed only if it is REALLY visible (display /
 * visibility / rect check — media queries decide per device) and its `when`
 * predicate passes. Runs once per page load; honors prefers-reduced-motion.
 *
 * Trigger: `loader:hidden` document event (dispatched by the search-page
 * loader in templates/partials/loader/_loader-readiness.html) or, if the
 * loader is already gone / absent, a plain post-DOMContentLoaded start.
 *
 * CSS side: `.attention-pulse` in static/css/utils/animations.css.
 * PULSE_TOTAL_MS must stay in sync with that animation (duration x iterations).
 */

const INITIAL_DELAY_MS = 800;    // let layout/FOUC settle after the loader fades
const DELAY_BETWEEN_MS = 1150;   // stagger between pulse STARTS (reads as a story)
const PULSE_TOTAL_MS = 2100;     // 1.05s x 2 iterations in CSS + sync (see animations.css)

// Attention priority order, not DOM order.
const PULSE_TARGETS = [
    {
        // Mobile filter toggle: when filters are collapsed the funnel button is
        // the only hint that filters exist at all — pulse it on load.
        selector: '[data-filter-toggle]',
        when: () => {
            const filtersDiv = document.getElementById('filters-div');
            return !!filtersDiv && !filtersDiv.classList.contains('filters-expanded');
        }
    }
];

let hasRun = false;

function dbg(msg, data) {
    try { if (window.debugMode === '1' || window.__pulseDebug) console.log('[AttentionPulse] ' + msg, data || ''); } catch (e) {}
}

function prefersReducedMotion() {
    try { return window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch (e) { return false; }
}

// Real visibility, not just !hidden: display/visibility chain + on-screen rect.
function isReallyVisible(el) {
    if (!el) return false;
    const style = getComputedStyle(el);
    if (style.display === 'none' || style.visibility === 'hidden' || parseFloat(style.opacity) === 0) return false;
    if (el.offsetParent === null && style.position !== 'fixed') return false;
    const rect = el.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return false;
    if (rect.bottom < 0 || rect.top > (window.innerHeight || document.documentElement.clientHeight)) return false;
    return true;
}

function pulseElement(el) {
    el.classList.remove('attention-pulse');
    void el.offsetWidth; // force reflow so the animation can retrigger
    el.classList.add('attention-pulse');
    setTimeout(() => { el.classList.remove('attention-pulse'); }, PULSE_TOTAL_MS + 100);
}

function runSequence() {
    let startedCount = 0;
    PULSE_TARGETS.forEach((target) => {
        const el = document.querySelector(target.selector);
        if (!el) return;
        const delay = INITIAL_DELAY_MS + startedCount * DELAY_BETWEEN_MS;
        startedCount++;
        setTimeout(() => {
            try {
                if (!isReallyVisible(el)) { dbg('skip (not visible)', target.selector); return; }
                if (typeof target.when === 'function' && !target.when()) { dbg('skip (when=false)', target.selector); return; }
                dbg('pulse', target.selector);
                pulseElement(el);
            } catch (e) { dbg('error', e && e.message); }
        }, delay);
    });
}

function startOnce() {
    if (hasRun) { dbg('startOnce ignored (already ran)'); return; }
    hasRun = true;
    dbg('startOnce');
    runSequence();
}

export function initAttentionPulse() {
    if (prefersReducedMotion()) { dbg('init skipped (reduced motion)'); return; }
    dbg('init', { loaderState: !!window.__loaderState, loaderHidden: window.__loaderState && window.__loaderState.loaderHidden });

    // Loader already hidden (or page has no loader state at all) → start now.
    const loaderState = window.__loaderState;
    if (!loaderState || loaderState.loaderHidden) {
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', startOnce, { once: true });
        } else {
            startOnce();
        }
        return;
    }

    // Otherwise wait for the loader to actually leave the screen.
    document.addEventListener('loader:hidden', startOnce, { once: true });

    // Belt-and-braces: poll the loader flag too. Covers deployments where the
    // template dispatching 'loader:hidden' is still Jinja-cached (prod caches
    // templates until service restart) and any page lacking the event.
    const poll = setInterval(() => {
        if (hasRun) { clearInterval(poll); return; }
        if (window.__loaderState && window.__loaderState.loaderHidden) {
            clearInterval(poll);
            startOnce();
        }
    }, 400);
    setTimeout(() => clearInterval(poll), 90000);
}

if (typeof window !== 'undefined') { window.initAttentionPulse = initAttentionPulse; }

export default initAttentionPulse;
